import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

const ModernColorBlockTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#6B7280';
  const secondaryColor = settings.theme?.secondary_color || '#9CA3AF';
  const fontFamily = settings.typography?.font_family || 'Inter, sans-serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    if (num === 0) return 'Zero Rupees Only';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template modern-colorblock" style={{ fontFamily, maxWidth: '210mm', margin: '0 auto', background: '#fff' }}>
      <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#fff', padding: '40px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '300px', opacity: 0.1, background: 'radial-gradient(circle, #fff 2px, transparent 2px)', backgroundSize: '30px 30px' }}></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
          <div>
            {settings.branding?.logo_url && (
              <div style={{ background: '#fff', padding: '10px', borderRadius: '8px', display: 'inline-block', marginBottom: '15px' }}>
                <img src={settings.branding.logo_url} alt="Logo" style={{ maxHeight: '60px' }} />
              </div>
            )}
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
              {settings.branding?.company_name || 'COMPANY NAME'}
            </h1>
            <p style={{ fontSize: '11px', opacity: 0.95, lineHeight: '1.6', maxWidth: '400px', marginTop: '8px' }}>
              {settings.branding?.address}<br/>
              Phone: {settings.branding?.phone} | Email: {settings.branding?.email}<br/>
              GSTIN: {settings.branding?.gstin}
            </p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', padding: '20px', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '2px', marginBottom: '8px', opacity: 0.9 }}>QUOTATION</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>{document.quotation_no || 'QT-001'}</div>
            <div style={{ fontSize: '11px', opacity: 0.95 }}>Date: {new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN')}</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '40px' }}>
        <div style={{ background: '#F3F4F6', borderLeft: `5px solid ${primaryColor}`, padding: '20px', marginBottom: '30px', borderRadius: '0 8px 8px 0' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: primaryColor, marginBottom: '12px' }}>BILL TO</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>{document.party?.name || 'Customer Name'}</div>
          {document.party?.phone && <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>Phone: {document.party.phone}</div>}
          {document.party?.billing_address && (
            <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.6', marginTop: '8px' }}>
              {document.party.billing_address.line1}, {document.party.billing_address.city}, {document.party.billing_address.state} - {document.party.billing_address.pincode}
            </div>
          )}
          {document.party?.gstin && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '6px' }}>GSTIN: {document.party.gstin}</div>}
        </div>

        <div style={{ marginBottom: '30px' }}>
          <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#fff', padding: '12px 15px', borderRadius: '8px 8px 0 0', display: 'flex', fontSize: '11px', fontWeight: '700', letterSpacing: '1px' }}>
            <div style={{ width: '5%' }}>#</div>
            <div style={{ width: settings.toggles?.show_hsn ? '40%' : '50%' }}>ITEM</div>
            {settings.toggles?.show_hsn && <div style={{ width: '15%', textAlign: 'center' }}>HSN</div>}
            <div style={{ width: '15%', textAlign: 'right' }}>QTY</div>
            <div style={{ width: '15%', textAlign: 'right' }}>RATE</div>
            <div style={{ width: '15%', textAlign: 'right' }}>AMOUNT</div>
          </div>
          {(document.items || []).map((item, index) => (
            <div key={index} style={{ display: 'flex', padding: '15px', borderBottom: '1px solid #E5E7EB', background: index % 2 === 0 ? '#FAFAFA' : '#fff' }}>
              <div style={{ width: '5%', fontSize: '11px', color: '#9CA3AF', fontWeight: '600' }}>{index + 1}</div>
              <div style={{ width: settings.toggles?.show_hsn ? '40%' : '50%', fontSize: '13px', color: '#1F2937', fontWeight: '600' }}>
                {item.name}
                {item.description && <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '3px' }}>{item.description}</div>}
              </div>
              {settings.toggles?.show_hsn && <div style={{ width: '15%', textAlign: 'center', fontSize: '11px', color: '#6B7280' }}>{item.hsn || '-'}</div>}
              <div style={{ width: '15%', textAlign: 'right', fontSize: '12px', color: '#6B7280' }}>{item.quantity} {item.unit || 'PCS'}</div>
              <div style={{ width: '15%', textAlign: 'right', fontSize: '12px', color: '#6B7280' }}>{formatCurrency(item.unit_price)}</div>
              <div style={{ width: '15%', textAlign: 'right', fontSize: '13px', color: '#1F2937', fontWeight: '700' }}>{formatCurrency(item.amount)}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <div style={{ width: '450px' }}>
            <div style={{ background: '#F9FAFB', borderRadius: '16px', padding: '20px', border: '2px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: '#6B7280', fontWeight: '500' }}>Subtotal</span>
                <span style={{ fontWeight: '600', color: '#1F2937' }}>{formatCurrency(document.totals?.subtotal || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: '#6B7280', fontWeight: '500' }}>Discount</span>
                <span style={{ fontWeight: '600', color: '#DC2626' }}>- {formatCurrency(document.totals?.discount_total || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', paddingTop: '8px', borderTop: '1px solid #E5E7EB' }}>
                <span style={{ color: '#6B7280', fontWeight: '600' }}>Taxable Amount</span>
                <span style={{ fontWeight: '700', color: '#1F2937' }}>{formatCurrency(document.totals?.taxable_amount || 0)}</span>
              </div>
              {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
                <React.Fragment key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', paddingLeft: '10px' }}>
                    <span style={{ color: '#6B7280' }}>CGST @ {tax.rate / 2}%</span>
                    <span style={{ fontWeight: '500', color: '#1F2937' }}>{formatCurrency(tax.amount / 2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', paddingLeft: '10px' }}>
                    <span style={{ color: '#6B7280' }}>SGST @ {tax.rate / 2}%</span>
                    <span style={{ fontWeight: '500', color: '#1F2937' }}>{formatCurrency(tax.amount / 2)}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, borderRadius: '16px', padding: '20px', marginTop: '10px', color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: document.payment?.received_amount ? '10px' : '0' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', letterSpacing: '1px' }}>TOTAL</span>
                <span style={{ fontSize: '24px', fontWeight: '900' }}>{formatCurrency(document.totals?.grand_total || 0)}</span>
              </div>
              {document.payment?.received_amount && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.95, marginBottom: '4px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '8px' }}>
                    <span>Received:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.payment.received_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.95 }}>
                    <span>Balance:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: '#F9FAFB', border: `1px solid ${primaryColor}`, borderRadius: '8px', padding: '15px', marginBottom: '30px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: primaryColor, marginBottom: '6px', letterSpacing: '1px' }}>AMOUNT IN WORDS</div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1F2937', textTransform: 'uppercase' }}>
            {numberToWords(document.totals?.grand_total || 0)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '20px', paddingTop: '30px', borderTop: `3px solid ${primaryColor}` }}>
          {document.terms?.notes_public && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor, marginBottom: '8px', letterSpacing: '1px' }}>TERMS & CONDITIONS</div>
              <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.8' }}>{document.terms.notes_public}</div>
            </div>
          )}
          {settings.toggles?.show_bank_details && settings.bank_details && (
            <div style={{ width: '300px', background: '#F9FAFB', border: `1px solid ${primaryColor}`, borderRadius: '8px', padding: '15px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor, marginBottom: '8px', letterSpacing: '1px' }}>BANK DETAILS</div>
              <div style={{ fontSize: '10px', color: '#6B7280', lineHeight: '1.8' }}>
                <div><strong>Bank:</strong> {settings.bank_details.bank_name}</div>
                <div><strong>A/C:</strong> {settings.bank_details.account_number}</div>
                <div><strong>IFSC:</strong> {settings.bank_details.ifsc}</div>
              </div>
            </div>
          )}
        </div>

        {settings.toggles?.show_signature && (
          <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>{settings.qr_code_data && <QRCode value={settings.qr_code_data} size={80} />}</div>
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <div style={{ borderTop: `2px solid ${primaryColor}`, paddingTop: '10px', minWidth: '200px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor }}>AUTHORIZED SIGNATURE</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernColorBlockTheme;